import _ from 'lodash';
import { OrgConfig, VisitResourceRequirement } from "~/providers/OrgConfigProvider";
import { CommandIndexProvidersQuery } from '~/graphql/__generated__/graphql';
import { 
  getAssignableProvidersForRequirement,
  SelectProviderData,
  defaultProviderData
} from '~/utils/visitValidationUtils';

export interface ProviderSelectItem {
  value: number;
  label: string;
  teamProviders: { teamId: number }[];
}

export interface GroupedProviders {
  teamMatches: ProviderSelectItem[];
  noTeamMatches: ProviderSelectItem[];
  referenceProviderName?: string;
}

export interface ReferenceProvider {
  providerId: number;
  providerName: string;
  vrrId: number;
}

export const getRefProvider = (
  currentVrrId: number,
  providerData: SelectProviderData[],
  providersList: CommandIndexProvidersQuery["commandIndexProviders"]["providers"]
): ReferenceProvider | null => {
  const refData = providerData.find(pd => 
    pd._vrrId !== currentVrrId && pd.resourceId && pd.resourceId !== ''
  );
  if (!refData?.resourceId) return null;

  const providerId = parseInt(refData.resourceId);
  if (isNaN(providerId)) return null;

  const provider = _.find(providersList, { id: providerId });
  if (!provider) return null;

  return {
    providerId,
    vrrId: currentVrrId,
    providerName: `${provider.firstName} ${provider.lastName}`
  };
};

export const getTeams = (
  providerId: number,
  orgConfig: OrgConfig
): number[] => {
  if (!orgConfig?.teamProvider) return [];
  
  return _(orgConfig.teamProvider)
    .filter({ providerId })
    .map('teamId')
    .value();
};

export const shareTeam = (
  providerId1: number,
  providerId2: number,
  orgConfig: OrgConfig
): boolean => {
  const teams1 = getTeams(providerId1, orgConfig);
  const teams2 = getTeams(providerId2, orgConfig);
  
  return !_.isEmpty(_.intersection(teams1, teams2));
};

export const groupByTeam = (
  availableProviders: ProviderSelectItem[],
  currentVrrId: number,
  providerData: SelectProviderData[],
  providersList: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  orgConfig: OrgConfig
): GroupedProviders => {
  const refProvider = getRefProvider(currentVrrId, providerData, providersList);
  
  if (!refProvider || !orgConfig?.teamProvider) {
    return {
      teamMatches: [],
      noTeamMatches: _.sortBy(availableProviders, 'label'),
      referenceProviderName: undefined
    };
  }

  const refTeamIds = getTeams(refProvider.providerId, orgConfig);
  
  if (_.isEmpty(refTeamIds)) {
    return {
      teamMatches: [],
      noTeamMatches: _.sortBy(availableProviders, 'label'),
      referenceProviderName: undefined
    };
  }

  const excludeRefProvider = (p: ProviderSelectItem) => p.value !== refProvider.providerId;
  const filteredProviders = _.filter(availableProviders, excludeRefProvider);

  const { teamMatches, noTeamMatches } = _.groupBy(filteredProviders, provider => {
    const providerTeamIds = _.map(provider.teamProviders, 'teamId');
    return !_.isEmpty(_.intersection(refTeamIds, providerTeamIds)) ? 'teamMatches' : 'noTeamMatches';
  });

  if (_.isEmpty(teamMatches)) {
    return {
      teamMatches: [],
      noTeamMatches: _.sortBy(availableProviders, 'label'),
      referenceProviderName: undefined
    };
  }

  return {
    teamMatches: _.sortBy(teamMatches || [], 'label'),
    noTeamMatches: _.sortBy(noTeamMatches || [], 'label'),
    referenceProviderName: _.first(refProvider.providerName.split(' '))
  };
};

export const hasTeamMismatch = (
  visitResourceRequirements: VisitResourceRequirement[],
  providerData: SelectProviderData[],
  orgConfig: OrgConfig
): boolean => {
  if (!orgConfig?.teamProvider || visitResourceRequirements.length <= 1) return false;
  
  const selectedProviders = _(providerData)
    .filter(pd => Boolean(pd.resourceId && pd.resourceId !== ''))
    .map(pd => parseInt(pd.resourceId))
    .filter(id => !isNaN(id))
    .value();
  
  if (selectedProviders.length <= 1) return false;
  
  const hasAnyWithTeams = _.some(selectedProviders, providerId => 
    !_.isEmpty(getTeams(providerId, orgConfig))
  );
  
  if (!hasAnyWithTeams) return false;
  
  // Check all pairs of providers for team sharing
  for (let i = 0; i < selectedProviders.length; i++) {
    for (let j = i + 1; j < selectedProviders.length; j++) {
      if (!shareTeam(selectedProviders[i], selectedProviders[j], orgConfig)) {
        return true;
      }
    }
  }
  
  return false;
};

export const getAutoAssignment = (
  vrr: VisitResourceRequirement,
  currentProviderData: SelectProviderData[],
  providersList: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  shifts: CommandIndexProvidersQuery["shift"],
  startTime: string,
  endTime: string,
  timezone: string | null | undefined,
  strictShiftFiltering: boolean,
  orgConfig: OrgConfig
) => {
  const currentData = _.find(currentProviderData, { _vrrId: vrr.id });
  
  if (currentData?.resourceId && currentData.resourceId !== '') {
    return null;
  }
  
  const filteredProviders = getAssignableProvidersForRequirement(
    providersList, shifts, vrr, startTime, endTime, timezone, strictShiftFiltering
  );
  
  const availableProviders: ProviderSelectItem[] = _.map(filteredProviders, provider => ({
    value: provider.id,
    label: `${provider.firstName} ${provider.lastName}`,
    teamProviders: _.map(provider.teamProviders, tp => ({ teamId: tp.teamId })),
  }));

  const groupedProviders = groupByTeam(
    availableProviders, 
    vrr.id, 
    currentProviderData, 
    providersList, 
    orgConfig
  );
  
  if (groupedProviders.teamMatches.length === 1) {
    const provider = groupedProviders.teamMatches[0];
    const refProvider = getRefProvider(vrr.id, currentProviderData, providersList);
    
    if (refProvider) {
      const sharedTeamIds = getTeams(refProvider.providerId, orgConfig);
      const providerTeamIds = getTeams(provider.value, orgConfig);
      const sharedTeamId = _.first(_.intersection(sharedTeamIds, providerTeamIds));
      
      return {
        vrr,
        providerId: provider.value,
        teamId: sharedTeamId || 0,
        updatedData: {
          ...defaultProviderData,
          ...currentData,
          _vrrId: vrr.id,
          resourceId: provider.value.toString(),
        }
      };
    }
  }
  
  return null;
};
